// ── 세금계산서 탭 콘텐츠 ──────────────────────────────────────────────────────
import React, { useState } from 'react';
import { FilterBar } from './FilterBar';
import {
  fmtNum, fmtDate,
  tblWrap, thStyle, tdStyle, rowCells,
  btnPrimary, btnSecondary, btnEdit, btnDanger, btnGroup,
} from './styles';
import { apiClient } from '../../../api/apiClient';

// 세금계산서 구분 배지 색상
const TYPE_COLOR = {
  '청구': { bg: '#fef3c7', color: '#92400e' },
  '영수': { bg: '#dcfce7', color: '#15803d' },
  '취소': { bg: '#fee2e2', color: '#b91c1c' },
  '수정': { bg: '#dbeafe', color: '#1d4ed8' },
};

/**
 * 세금계산서 탭 콘텐츠 (업로드 이력 + 목록)
 * Props:
 *   invoices, taxBatches
 *   filterStart, setFilterStart, filterEnd, setFilterEnd
 *   loadInvoices, loadTaxInvoices
 *   invoiceFileRef, invoiceUploading, handleInvoiceFileSelect
 *   setModal, handleDelete, salesUsers
 *   onDeleteBatch   — (batchId) 배치 삭제 핸들러
 *   onDownloadBatch — (batchId, fileName) 배치 다운로드 핸들러
 */
export function TabInvoices({
  invoices, taxBatches,
  filterStart, setFilterStart, filterEnd, setFilterEnd,
  loadInvoices, loadTaxInvoices,
  invoiceFileRef, invoiceUploading, handleInvoiceFileSelect,
  setModal, handleDelete, salesUsers,
  auditTableName = 'revenue_tax_invoices', // 감사 로그 테이블명 (DOT/EXT는 다른 값 전달)
  onDeleteBatch,
  onDownloadBatch,
}) {
  // 기간 내 발행 합계
  const total = invoices.reduce((s, r) => s + Number(r.total_amount || 0), 0);

  // 업로드 이력 펼침/접힘 — 기본은 최신 1건만 표시
  const [batchExpanded, setBatchExpanded] = useState(false);
  const visibleBatches = batchExpanded ? taxBatches : taxBatches.slice(0, 1);

  return (
    <>
      {/* 업로드 이력 섹션 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 6 }}>업로드 이력</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
          {/* XML / 홈택스 파일 통합 업로드 버튼 */}
          <button style={btnPrimary}
            onClick={() => invoiceFileRef.current?.click()}
            disabled={invoiceUploading}>
            {invoiceUploading ? '업로드 중...' : '📤 파일 업로드'}
          </button>
          <input
            ref={invoiceFileRef}
            type="file"
            accept=".xml,.xls,.xlsx,.csv,.txt"
            style={{ display: 'none' }}
            onChange={handleInvoiceFileSelect}
          />
          <button style={{ ...btnSecondary, marginLeft: 'auto' }}
            onClick={() => setModal({ type: 'auditlog', tableName: auditTableName, title: '세금계산서' })}>
            이력
          </button>
        </div>

        {/* 업로드 이력 테이블 — 기본 최신 1건, 펼치면 전체 */}
        {taxBatches.length > 0 && (
          <div style={tblWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['업로드일시','파일명','전체','신규','중복스킵','업로더',''].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleBatches.map((b, i) => {
                  const c = rowCells(i, visibleBatches.length); // 마지막 행 밑줄 제거
                  return (
                  <tr key={b.id}>
                    <td style={c.td}>{b.uploaded_at}</td>
                    <td style={c.td}>{b.file_name}</td>
                    <td style={{ ...c.td, textAlign: 'center' }}>{b.total_rows}</td>
                    <td style={{ ...c.td, textAlign: 'center', color: '#15803d' }}>{b.new_count}</td>
                    <td style={{ ...c.td, textAlign: 'center', color: 'var(--text-muted)' }}>{b.skipped_count}</td>
                    <td style={c.td}>{b.uploaded_by_name || '-'}</td>
                    {/* CSV 다운로드 + 삭제 버튼 — 오른쪽 정렬 */}
                    <td style={c.btn}>
                      <div style={btnGroup}>
                        {onDownloadBatch && (
                          <button
                            style={btnEdit}
                            onClick={() => onDownloadBatch(b.id, b.file_name)}>
                            CSV
                          </button>
                        )}
                        {onDeleteBatch && (
                          <button style={btnDanger}
                            onClick={() => onDeleteBatch(b.id)}>
                            삭제
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {/* 펼치기/접기 버튼 — 이력이 2건 이상일 때만 표시 */}
            {taxBatches.length > 1 && (
              <button
                onClick={() => setBatchExpanded((v) => !v)}
                style={{
                  width: '100%', padding: '6px 0', border: 'none',
                  background: 'var(--bg-subtle)', color: 'var(--text-muted)',
                  fontSize: 11, cursor: 'pointer',
                  borderTop: '1px solid var(--border)',
                  borderRadius: '0 0 8px 8px',
                }}>
                {batchExpanded
                  ? '▲ 접기'
                  : `▼ 이전 이력 ${taxBatches.length - 1}건 더 보기`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 세금계산서 목록 */}
      <FilterBar
        filterStart={filterStart} setFilterStart={setFilterStart}
        filterEnd={filterEnd}     setFilterEnd={setFilterEnd}
        onSearch={loadInvoices}
      />
      <div style={{ fontSize: 12, color: 'var(--text-sub)', margin: '8px 0' }}>
        총 {invoices.length}건 / 발행 합계 <strong>{fmtNum(total)}</strong>원
      </div>
      <div style={tblWrap}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['발행일','구분','상호','공급가액','세액','발행금액','미입금액','마케터','입금 건수','비고',''].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr>
                <td colSpan={11} style={{ ...tdStyle, borderBottom: 'none', textAlign: 'center', color: 'var(--text-muted)' }}>
                  데이터 없음
                </td>
              </tr>
            )}
            {invoices.map((r, i) => {
              const c = rowCells(i, invoices.length); // 마지막 행 밑줄 제거
              const unpaid     = Number(r.unpaid_amount ?? r.total_amount);
              const typeColor  = TYPE_COLOR[r.issue_type] || { bg: 'var(--bg-subtle)', color: 'var(--text-sub)' };
              // 미입금액 셀: 완납/초과/남음
              const unpaidCell = unpaid === 0
                ? <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>✅ 완납</span>
                : unpaid < 0
                  ? <span style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>🔴 {fmtNum(unpaid)}원</span>
                  : <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 600 }}>{fmtNum(unpaid)}원</span>;

              return (
                <tr key={r.id}>
                  <td style={c.td}>{fmtDate(r.issue_date)}</td>
                  <td style={c.td}>
                    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4,
                      background: typeColor.bg, color: typeColor.color }}>
                      {r.issue_type}
                    </span>
                  </td>
                  <td style={c.td}>{r.customer_name || '-'}</td>
                  <td style={c.amt}>{fmtNum(r.supply_amount)}</td>
                  <td style={c.amt}>{fmtNum(r.tax_amount)}</td>
                  <td style={c.amt}><strong>{fmtNum(r.total_amount)}</strong>원</td>
                  <td style={c.amt}>{unpaidCell}</td>

                  {/* 마케터: 없으면 클릭으로 바로 선택 가능 */}
                  <td style={c.td}>
                    {r.user_name ? (
                      <span style={{ fontSize: 12 }}>{r.user_name}</span>
                    ) : (
                      <select
                        style={{ fontSize: 11, padding: '2px 4px', border: '1px dashed var(--text-muted)',
                          borderRadius: 4, background: 'var(--bg-subtle)', color: 'var(--text-muted)', cursor: 'pointer' }}
                        defaultValue=""
                        onChange={async (e) => {
                          if (!e.target.value) return;
                          const res = await apiClient.put(
                            `/api/admin/revenue/tax-invoices/${r.id}`,
                            { user_id: e.target.value }
                          );
                          if (res.data?.success) { loadInvoices(); loadTaxInvoices(); }
                        }}>
                        <option value="">마케터 선택</option>
                        {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    )}
                  </td>

                  {/* 연결된 입금 건수 */}
                  <td style={{ ...c.td, textAlign: 'center' }}>
                    {r.payment_count > 0
                      ? <span style={{ fontSize: 11, color: 'var(--std-accent-text, #1d7de6)' }}>{r.payment_count}건 ({fmtNum(r.payment_total)}원)</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>-</span>
                    }
                  </td>

                  <td style={c.td}>{r.note || '-'}</td>
                  {/* 수정/삭제 버튼 — 오른쪽 정렬 */}
                  <td style={c.btn}>
                    <div style={btnGroup}>
                      <button style={btnEdit}
                        onClick={() => setModal({ type: 'invoice', data: r })}>수정</button>
                      <button style={btnDanger}
                        onClick={() => handleDelete('tax-invoices', r.id, '세금계산서')}>삭제</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
